// Solomon Portal v1 — runtime env access
// Validated with zod so missing/empty values fail fast at module-load time
// instead of mysteriously breaking in the auth flow.
//
// Beacon-retirement T+30 cleanup contract (ADR-036). On or after
// 2026-06-08 — the BEACON_RETIREMENT_CUTOFF_ISO mirrored in
// `scripts/validate-prod-env.ts` — the `VITE_HELPSCOUT_BEACON_ID`
// field below MUST be dropped as part of the same commit that:
//   - Deletes `src/lib/beacon.ts`
//   - Deletes `src/components/support/BeaconEmbed.tsx`
//   - Deletes the beacon-related tests in `src/test/`
//   - Removes `VITE_HELPSCOUT_BEACON_ID` from `.env.production`
//   - Removes the Beacon entries from `.env.example`
//   - Removes `beacon-signature` from the `deploy:functions:dev`
//     and `deploy:functions:prod` scripts in `package.json`
//   - Removes `beacon-signature` from `PROVENANCE_FUNCTIONS` in
//     `scripts/security/edge-functions-smoke.ts`
//   - Undeploys the `beacon-signature` Edge Function (one-time
//     `supabase functions delete beacon-signature --project-ref <ref>`
//     against both dev and prod)
// The build-time validator already FORBIDS the env key after the
// cutoff (ADR-036 T+30 closeout); if `.env.production` drops the key
// but this schema still requires it, the SPA will throw at module
// init. Land all of the above changes in the same commit so the
// runtime, build-time, and deploy surfaces remain consistent.
// Tracking: issue #16, ADR-036.

import { z } from 'zod';

const EnvSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(20),
  // T+30 cleanup field (ADR-036) — see header comment.
  VITE_HELPSCOUT_BEACON_ID: z.string().min(8),
});

const rawEnv = import.meta.env as Record<string, string | undefined>;

const parsed = EnvSchema.safeParse({
  VITE_SUPABASE_URL: rawEnv.VITE_SUPABASE_URL,
  VITE_SUPABASE_ANON_KEY: rawEnv.VITE_SUPABASE_ANON_KEY,
  // T+30 cleanup field (ADR-036) — see header comment.
  VITE_HELPSCOUT_BEACON_ID: rawEnv.VITE_HELPSCOUT_BEACON_ID,
});

if (!parsed.success) {
  // We deliberately throw at module init. Cloudflare Pages preview and prod
  // builds must have all env vars set; missing values should fail the build,
  // not silently render an unauthenticated page.
  console.error('Env validation failed:', parsed.error.flatten());
  throw new Error('Solomon Portal: required environment variables missing — see .env.example.');
}

export const env = parsed.data;
